export type RawSection = { title?: string; instructions?: string; content?: string; text?: string };

/**
 * Flattens a template's sections map into the single-body string used by the
 * AI Blueprint editor.
 *
 * Old (Flutter-era) custom templates can have multiple sections with arbitrary
 * titles. The new AI Blueprint format keeps a single `instructions` body. This
 * helper converts old -> new on load, while leaving already-new templates
 * unchanged (a single section with an empty/missing title returns only its
 * body).
 *
 * Rules:
 * - Iterate sections in their original (insertion) order.
 * - For each section emit `<title>\n<content>` when both are present, the title
 *   alone when there is no content, or the content alone when there is no
 *   title.
 * - Skip sections that are completely empty.
 * - Join sections with `\n\n`.
 * - Prefers the AI Blueprint `instructions` field.
 * - Falls back to `content`, then the legacy `text` field.
 */
export function flattenSectionsToInstructions(
  sections: Record<string, RawSection> | undefined | null
): string {
  if (!sections) return '';
  const parts: string[] = [];
  for (const s of Object.values(sections)) {
    const title = (s?.title ?? '').trim();
    const body = (s?.instructions ?? s?.content ?? s?.text ?? '').trim();
    if (!title && !body) continue;
    if (title && body) {
      parts.push(`${title}\n${body}`);
    } else if (title) {
      parts.push(title);
    } else {
      parts.push(body);
    }
  }
  return parts.join('\n\n');
}
